import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import api from '../utils/api';
import { formatNumber, formatRelativeTime } from '../utils/helpers';
import {
  Users,
  FileText,
  CreditCard,
  Activity,
  TrendingUp,
  TrendingDown,
  Calendar,
  Clock,
  Server,
  AlertTriangle
} from 'lucide-react';

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [transcriptStats, setTranscriptStats] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('30days');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [showCustomDates, setShowCustomDates] = useState(false);

  useEffect(() => {
    fetchDashboardData();
  }, [dateRange, customStartDate, customEndDate]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Initialize with default data in case API calls fail
      let statsData = {
        userStats: {
          totalUsers: 0,
          activeUsers: 0,
          adminUsers: 0,
          totalUsage: 0
        },
        planDistribution: [],
        recentActivity: []
      };
      let transcriptStatsData = {
        total: 0,
        completed: 0,
        processing: 0,
        failed: 0,
        pending: 0,
        unavailable: 0
      };
      let logsData = [];
      
      // Try to fetch admin stats with date range
      try {
        let statsUrl = `/admin/stats?dateRange=${dateRange}`;
        if (dateRange === 'custom' && customStartDate && customEndDate) {
          statsUrl = `/admin/stats?dateRange=custom&startDate=${customStartDate}&endDate=${customEndDate}`;
        }
        const statsResponse = await api.get(statsUrl);
        if (statsResponse.data) {
          statsData = { ...statsData, ...statsResponse.data };
        }
      } catch (statsError) {
        console.warn('Failed to fetch stats, using defaults:', statsError.message);
      }
      
      // Try to fetch transcript stats
      try {
        const transcriptResponse = await api.get('/admin/transcripts/stats');
        if (transcriptResponse.data) {
          transcriptStatsData = { ...transcriptStatsData, ...transcriptResponse.data };
        }
      } catch (transcriptError) {
        console.warn('Failed to fetch transcript stats, using defaults:', transcriptError.message);
      }
      
      // Try to fetch recent logs
      try {
        const logsResponse = await api.get('/admin/logs?limit=10');
        if (logsResponse.data && logsResponse.data.logs) {
          logsData = logsResponse.data.logs;
        }
      } catch (logsError) {
        console.warn('Failed to fetch logs, using defaults:', logsError.message);
      }
      
      setStats(statsData);
      setTranscriptStats(transcriptStatsData);
      setRecentActivity(logsData);
      
    } catch (error) {
      console.error('Dashboard data fetch error:', error);
      // Don't show error toast for dashboard data - use defaults instead
      setStats({
        userStats: {
          totalUsers: 0,
          activeUsers: 0,
          adminUsers: 0,
          totalUsage: 0
        },
        planDistribution: [],
        recentActivity: []
      });
      setTranscriptStats({
        total: 0,
        completed: 0,
        processing: 0,
        failed: 0,
        pending: 0,
        unavailable: 0
      });
      setRecentActivity([]);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner w-8 h-8"></div>
      </div>
    );
  }

  const statCards = [
    {
      title: 'Total Users',
      value: formatNumber(stats?.userStats?.totalUsers || 0),
      change: '+12%',
      changeType: 'increase',
      icon: Users,
      color: 'bg-blue-500',
    },
    {
      title: 'Active Users',
      value: formatNumber(stats?.userStats?.activeUsers || 0),
      change: '+8%',
      changeType: 'increase',
      icon: Activity,
      color: 'bg-green-500',
    },
    {
      title: 'Total Transcripts',
      value: formatNumber(transcriptStats?.total || 0),
      change: '+15%',
      changeType: 'increase',
      icon: FileText,
      color: 'bg-purple-500',
    },
    {
      title: 'Admin Users',
      value: formatNumber(stats?.userStats?.adminUsers || 0),
      change: '0%',
      changeType: 'neutral',
      icon: CreditCard,
      color: 'bg-orange-500',
    },
  ];

  const transcriptStatsCards = [
    {
      title: 'Completed',
      value: formatNumber(transcriptStats?.completed || 0),
      subtitle: 'Transcripts',
      icon: FileText,
      color: 'bg-green-500',
    },
    {
      title: 'Processing',
      value: formatNumber(transcriptStats?.processing || 0),
      subtitle: 'In Progress',
      icon: Clock,
      color: 'bg-blue-500',
    },
    {
      title: 'Failed',
      value: formatNumber(transcriptStats?.failed || 0),
      subtitle: 'Errors',
      icon: AlertTriangle,
      color: 'bg-red-500',
    },
    {
      title: 'Pending',
      value: formatNumber(transcriptStats?.pending || 0),
      subtitle: 'Waiting',
      icon: Calendar,
      color: 'bg-yellow-500',
    },
  ];

  const dateRangeOptions = [
    { value: 'today', label: 'Today' },
    { value: '7days', label: 'Last 7 Days' },
    { value: '30days', label: 'Last 30 Days' },
    { value: '90days', label: 'Last 90 Days' },
    { value: 'year', label: 'This Year' },
    { value: 'all', label: 'All Time' },
    { value: 'custom', label: 'Custom Range' },
  ];

  const handleDateRangeChange = (value) => {
    setDateRange(value);
    setShowCustomDates(value === 'custom');
    if (value !== 'custom') {
      setCustomStartDate('');
      setCustomEndDate('');
    }
  };

  const getDateRangeText = () => {
    const now = new Date();
    let startDate;
    
    switch (dateRange) {
      case 'today':
        return `Today (${now.toLocaleDateString()})`;
      case '7days':
        startDate = new Date(now);
        startDate.setDate(startDate.getDate() - 7);
        return `${startDate.toLocaleDateString()} - ${now.toLocaleDateString()}`;
      case '30days':
        startDate = new Date(now);
        startDate.setDate(startDate.getDate() - 30);
        return `${startDate.toLocaleDateString()} - ${now.toLocaleDateString()}`;
      case '90days':
        startDate = new Date(now);
        startDate.setDate(startDate.getDate() - 90);
        return `${startDate.toLocaleDateString()} - ${now.toLocaleDateString()}`;
      case 'year':
        return `Jan 1, ${now.getFullYear()} - ${now.toLocaleDateString()}`;
      case 'all':
        return 'All Time';
      case 'custom':
        if (customStartDate && customEndDate) {
          return `${new Date(customStartDate).toLocaleDateString()} - ${new Date(customEndDate).toLocaleDateString()}`;
        }
        return 'Select dates';
      default:
        return '';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="mt-1 text-sm text-gray-600">
            Welcome to the TokScript admin portal
          </p>
        </div>
        <div className="flex items-center space-x-4">
          <div className="flex flex-col items-end">
            {/* <label className="text-xs text-gray-500 mb-1">Date Range</label> */}
            <select
              value={dateRange}
              onChange={(e) => handleDateRangeChange(e.target.value)}
              className="input input-sm min-w-[180px]"
            >
              {dateRangeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <span className="text-xs text-gray-400 mt-1">{getDateRangeText()}</span>
          </div>
          {showCustomDates && (
            <div className="flex items-center space-x-2">
              <div className="flex flex-col">
                <label className="text-xs text-gray-500 mb-1">Start Date</label>
                <input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  className="input input-sm"
                  max={customEndDate || new Date().toISOString().split('T')[0]}
                />
              </div>
              <div className="flex flex-col">
                <label className="text-xs text-gray-500 mb-1">End Date</label>
                <input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  className="input input-sm"
                  min={customStartDate}
                  max={new Date().toISOString().split('T')[0]}
                />
              </div>
            </div>
          )}
          <div className="flex items-center space-x-2 text-sm text-gray-500">
            <Clock className="w-4 h-4" />
            <span>Last updated: {formatRelativeTime(new Date())}</span>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <div key={index} className="card">
              <div className="card-body">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                    <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                    <div className="flex items-center mt-1">
                      {stat.changeType === 'increase' ? (
                        <TrendingUp className="w-4 h-4 text-green-500 mr-1" />
                      ) : stat.changeType === 'decrease' ? (
                        <TrendingDown className="w-4 h-4 text-red-500 mr-1" />
                      ) : (
                        <div className="w-4 h-4 mr-1" />
                      )}
                      <span className={`text-sm font-medium ${
                        stat.changeType === 'increase' ? 'text-green-600' :
                        stat.changeType === 'decrease' ? 'text-red-600' :
                        'text-gray-500'
                      }`}>
                        {stat.change} from last month
                      </span>
                    </div>
                  </div>
                  <div className={`p-3 rounded-lg ${stat.color}`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Transcript Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {transcriptStatsCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <div key={index} className="card">
              <div className="card-body">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                    <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                    <p className="text-xs text-gray-500">{stat.subtitle}</p>
                  </div>
                  <div className={`p-3 rounded-lg ${stat.color}`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Plan Distribution */}
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-semibold text-gray-900">Plan Distribution</h3>
          </div>
          <div className="card-body">
            <div className="space-y-4">
              {stats?.planDistribution?.map((plan, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className={`w-3 h-3 rounded-full mr-3 ${
                      plan._id === 'free' ? 'bg-blue-500' :
                      plan._id === 'pro' ? 'bg-purple-500' :
                      plan._id === 'premium' ? 'bg-orange-500' :
                      plan._id === 'basic' ? 'bg-green-500' :
                      'bg-gray-500'
                    }`} />
                    <span className="text-sm font-medium text-gray-900 capitalize">
                      {plan._id}
                    </span>
                  </div>
                  <span className="text-sm text-gray-600">
                    {formatNumber(plan.count)} users
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-semibold text-gray-900">Recent Activity</h3>
          </div>
          <div className="card-body">
            <div className="space-y-3">
              {recentActivity.length > 0 ? (
                recentActivity.slice(0, 5).map((activity, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      <div className="w-2 h-2 bg-primary-500 rounded-full mt-2"></div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-900">
                        {activity.method} {activity.endpoint}
                      </p>
                      <p className="text-xs text-gray-500">
                        {activity.userId?.name || 'Unknown User'} • {formatRelativeTime(activity.timestamp)}
                      </p>
                    </div>
                    <div className={`px-2 py-1 text-xs rounded-full ${
                      activity.statusCode >= 200 && activity.statusCode < 300
                        ? 'bg-green-100 text-green-800'
                        : activity.statusCode >= 400
                        ? 'bg-red-100 text-red-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {activity.statusCode}
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-4">
                  <Server className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">No recent activity</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* System Health */}
      {/* <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-semibold text-gray-900">System Health</h3>
        </div>
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                <Server className="w-6 h-6 text-green-600" />
              </div>
              <p className="text-sm font-medium text-gray-900">Server Status</p>
              <p className="text-xs text-green-600">Online</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                <Activity className="w-6 h-6 text-blue-600" />
              </div>
              <p className="text-sm font-medium text-gray-900">API Response</p>
              <p className="text-xs text-blue-600">~200ms</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                <AlertTriangle className="w-6 h-6 text-orange-600" />
              </div>
              <p className="text-sm font-medium text-gray-900">Alerts</p>
              <p className="text-xs text-orange-600">0 Active</p>
            </div>
          </div>
        </div>
      </div> */}
    </div>
  );
};

export default Dashboard;